import 'react-phone-input-2/lib/material.css'

import { FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormLabel,
  Grid,
  makeStyles,
  TextField,
} from '@perk-ui/core'
import { useFormik } from 'formik'
import debounce from 'lodash/debounce'
import { LogOut } from 'react-feather'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'
import PhoneInput from 'react-phone-input-2'
import { useMutation, useQueryClient } from 'react-query'
import * as yup from 'yup'

import { useSitesContext } from '../../../components/SitesProvider'
import config from '../../../config/app-config'
import request from '../../../config/axios'
import {
  canadaTimeZones,
  countryList,
  siteSpecialties,
  unitedStatesTimeZones,
} from '../../../config/constants'
import AnimatedRedirect from '../../../features/animation/AnimatedRedirect'
import { useAuth } from '../../../features/auth/AuthContext'
import QueryKeys from '../../../features/query-hooks/QueryKeys'
import { useHistory } from '../../../utils/useHistory'
import FormContainer from '../components/FormContainer'
import FormHeader from '../components/FormHeader'

// Clinic Type options - will need to be updated based on Figma design
const clinicTypes = [
  'Private Practice',
  'Hospital',
  'Medical Center',
  'Clinic',
  'Urgent Care',
  'Specialty Center',
  'Ambulatory Surgery Center',
  'Imaging Center',
]

interface NewClinicFormData {
  name: string
  shortName: string
  clinicType: string
  country: string
  timeZone: string
  specialty: string[]
  searchAddress: string
  street: string
  unitApartmentSuite: string
  city: string
  stateProvince: string
  zipCode: string
  phone: string
}

const useStyles = makeStyles((theme) => ({
  formCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    padding: theme.spacing(3),
    width: '100%',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
  },
  formTitle: {
    fontSize: '1.75em',
    fontWeight: 700,
    color: '#212121',
    marginBottom: theme.spacing(2),
  },
  inputField: {
    marginBottom: theme.spacing(0),
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#FAFAFB',
      paddingRight: '9px !important',
      '& fieldset': {
        borderColor: '#E1E4E8',
        '& legend': {
          visibility: 'hidden',
          maxWidth: '0.01px',
        },
      },
      '&:hover fieldset': {
        borderColor: '#C4C9CF',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#212121',
      fontSize: '14px',
      fontWeight: 400,
      marginBottom: theme.spacing(1),
    },
    '& .MuiAutocomplete-input': {
      paddingLeft: '7px !important',
    },
    '& .MuiAutocomplete-endAdornment': {
      paddingRight: '7px !important',
    },
    '& .MuiInputBase-input': {
      height: '1em',
    },
  },
  submitButtonEnabled: {
    width: '100%',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 700,
    textTransform: 'none',
    backgroundColor: '#182C5A',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#0F1729',
    },
  },
  phoneFieldContainer: {
    position: 'relative',
    marginBottom: '12px',
    '& .special-label': {
      display: 'none',
    },
    '& .flag-dropdown': {
      border: 'none !important',
      backgroundColor: 'transparent !important',
      '&.open': {
        backgroundColor: 'transparent !important',
      },
    },
    '& .selected-flag': {
      backgroundColor: 'transparent !important',
      border: 'none !important',
      '&:hover': {
        backgroundColor: 'transparent !important',
      },
      '&.open': {
        backgroundColor: 'transparent !important',
      },
    },
    '& input.form-control': {
      width: '100%',
      border: '1px solid #E1E4E8',
      backgroundColor: '#FAFAFB',
      borderRadius: '8px',
      padding: '16px', // Adjust internal padding
      '&:hover': {
        borderColor: '#C4C9CF',
      },
      '&:focus': {
        outline: 'none',
        borderColor: theme.palette.primary.main,
        backgroundColor: '#FFFFFF',
      },
    },
    '& .react-tel-input .form-control': {
      padding: '16px !important',
      paddingLeft: '58px !important', // Add space for flag and country code
      minHeight: 'calc(1.4375em + 22px)', // Add 11px top + 11px bottom to match fieldset
    },
    '& .MuiFormLabel-root': {
      display: 'block',
      marginBottom: theme.spacing(1), // Natural flow like other labels
      color: '#212121',
      fontSize: '14px',
      fontWeight: 400,
    },
  },
}))

const newClinicValidationSchema = yup.object().shape({
  name: yup.string().required('Clinic name is required'),
  shortName: yup.string().required('Short name is required'),
  clinicType: yup.string().required('Clinic type is required'),
  country: yup.string().required('Country is required'),
  timeZone: yup.string().required('Time zone is required'),
  specialty: yup
    .array()
    .min(1, 'At least one specialty is required')
    .required('Specialty is required'),
  searchAddress: yup.string(), // Optional - used for Google Places search
  street: yup.string().required('Street address is required'),
  city: yup.string().required('City is required'),
  stateProvince: yup.string().required('State/Province is required'),
  zipCode: yup.string().when('country', {
    is: 'canada',
    then: yup
      .string()
      .length(6, 'Postal code should be 6 characters')
      .required('Postal code is required'),
    otherwise: yup
      .string()
      .length(5, 'ZIP code should be 5 digits')
      .required('ZIP code is required'),
  }),
  phone: yup.string(), // Optional
  unitApartmentSuite: yup.string(), // Optional
})

interface CreateNewClinicStepProps {
  onNext?: (data: Record<string, unknown>) => void
}

const CreateNewClinicStep: FC<CreateNewClinicStepProps> = ({ onNext }) => {
  const classes = useStyles()
  const { user, signOut } = useAuth()
  const { push } = useHistory()
  const queryClient = useQueryClient()
  const { sitesQuery, setCurrentSite } = useSitesContext()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isCreatingClinic, setIsCreatingClinic] = useState(false)
  const [specialtyDropdownOpen, setSpecialtyDropdownOpen] = useState(false)
  const specialtyInputRef = useRef<HTMLInputElement>(null)

  // Prevent existing users with sites from accessing this page
  if (sitesQuery.data && sitesQuery.data.length > 0) {
    return <AnimatedRedirect to="/home" />
  }

  // Check if we should show loading state immediately on component mount
  useEffect(() => {
    const isInCreationFlow = localStorage.getItem('isCreatingClinic') === 'true'
    if (isInCreationFlow) {
      setIsCreatingClinic(true)
      // Slight delay to ensure the component has mounted and then show the form
      setTimeout(() => {
        setIsCreatingClinic(false)
      }, 1000) // 1 second to show "Creating Your Clinic" before showing form
    }
  }, [])

  // Navigation functions
  const handleGoBack = () => {
    // Clear the localStorage flag and navigate back to search-clinic
    localStorage.removeItem('isCreatingClinic')
    push('/provider/search-clinic')
  }

  const handleSignOut = () => {
    signOut()
  }

  // Google Places setup for add-new form
  const [placeSelected, setPlaceSelected] = useState(false)
  const [completeManually] = useState(false)

  // Define interface for Google Places result to avoid namespace dependency
  interface PlaceResult {
    address_components?: Array<{
      long_name: string
      short_name: string
      types: string[]
    }>
    formatted_address?: string
    place_id?: string
  }

  const [googlePlace, setGooglePlace] = useState<PlaceResult | null>(null)

  const { placesService, placePredictions, getPlacePredictions } =
    usePlacesService({
      apiKey: config.googleMapsApiKey,
      options: {
        componentRestrictions: {
          country: completeManually ? [] : ['ca', 'us'],
        },
        fields: ['address_components'],
        types: ['address'],
      },
    })

  // Debounce Google Places API calls to prevent lag (300ms delay)
  const debouncedGetPlacePredictions = useMemo(
    () =>
      debounce((input: string) => {
        getPlacePredictions({
          input,
          componentRestrictions: {
            country: ['ca', 'us'],
          },
          types: ['address'],
        })
      }, 300),
    [getPlacePredictions],
  )

  // Create clinic mutation with Provider-specific requirements
  const createClinicMutation = useMutation(
    (vars: NewClinicFormData & { staffId: string }) => {
      const { staffId, shortName, clinicType, ...siteData } = vars
      const requestBody = {
        site: {
          ...siteData,
          short_name: shortName, // Convert camelCase to snake_case for backend
          clinic_type: clinicType, // Convert camelCase to snake_case for backend
        },
        staff_id: staffId, // Include staff ID for admin association
        activated: true, // Staff should be activated when creating new clinic
        is_admin: true, // Staff should be admin when creating new clinic
      }
      return request.post('/impacts/sites', requestBody).then((r) => {
        queryClient.invalidateQueries(QueryKeys.sitesAvailable())
        return r.data
      })
    },
  )

  // New clinic form setup
  const newClinicFormik = useFormik<NewClinicFormData>({
    initialValues: {
      name: '',
      shortName: '',
      clinicType: '',
      country: 'united_states_of_america',
      timeZone: '',
      specialty: [],
      searchAddress: '',
      street: '',
      unitApartmentSuite: '',
      city: '',
      stateProvince: '',
      zipCode: '',
      phone: '',
    },
    validationSchema: newClinicValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        setSubmitError(null) // Clear any previous errors

        // Get the staff ID from authenticated user
        const staffId = user?.staff?.id

        if (!staffId) {
          throw new Error('User not authenticated. Please log in to continue.')
        }

        const response = await createClinicMutation.mutateAsync({
          ...values,
          staffId: staffId, // Include staff ID for backend association
        })

        // Success: Get the new clinic (staff association already handled by create endpoint)
        const newClinic = response.site

        // Clear the old provider flag and isCreatingClinic flag to allow state transition
        localStorage.removeItem('providerCreatingNewClinic')
        localStorage.removeItem('isCreatingClinic')

        // Update cache immediately with known data to prevent race condition
        queryClient.setQueryData(QueryKeys.sitesAvailable(), [newClinic])

        // Update currentSite context immediately
        setCurrentSite(newClinic)

        // Force Provider component to re-evaluate state by invalidating and refetching
        setTimeout(() => {
          queryClient.invalidateQueries(QueryKeys.sitesAvailable())
        }, 50)

        // Update currentUser cache to show we have sites and are activated
        queryClient.setQueryData(
          QueryKeys.currentUser(),
          (oldUser: unknown) => {
            if (!oldUser || typeof oldUser !== 'object') return oldUser
            const user = oldUser as Record<string, unknown>
            const staff = user.staff as Record<string, unknown>
            return {
              ...user,
              staff: {
                ...staff,
                activated: true,
                sites: [newClinic],
                hasActiveSiteStaff: true,
              },
            }
          },
        )

        // Provider component will automatically transition to 'complete' state
        // based on updated cache data and cleared localStorage flag

        // Call onNext with clinic creation success if provided
        if (onNext) {
          onNext({
            clinicId: newClinic.id,
            clinicName: newClinic.name,
          })
        }
      } catch (error: unknown) {
        // Helper function to safely extract error message
        const getErrorMessage = (err: unknown): string => {
          if (err && typeof err === 'object') {
            const errorObj = err as Record<string, unknown>

            // Check for API response errors
            if (errorObj.response && typeof errorObj.response === 'object') {
              const response = errorObj.response as Record<string, unknown>
              if (response.data && typeof response.data === 'object') {
                const data = response.data as Record<string, unknown>
                if (data.errors && typeof data.errors === 'string') {
                  return data.errors
                }
                if (data.error && typeof data.error === 'string') {
                  return data.error
                }
              }
            }

            // Check for standard Error message
            if ('message' in errorObj && typeof errorObj.message === 'string') {
              return errorObj.message
            }
          }

          return 'Failed to create clinic. Please try again.'
        }

        const errorMessage = getErrorMessage(error)
        setSubmitError(errorMessage)
      }
    },
  })

  // Parse Google Places address components and auto-populate form fields
  useEffect(() => {
    if (googlePlace && placeSelected) {
      const result = googlePlace?.address_components
      if (result) {
        type AddressComponent = {
          long_name: string
          short_name: string
          types: string[]
        }

        const components = result as AddressComponent[]

        const country =
          components.find((component) => component.types.includes('country'))
            ?.short_name || ''
        const streetNumber =
          components.find((component) =>
            component.types.includes('street_number'),
          )?.short_name || ''
        const route =
          components.find((component) => component.types.includes('route'))
            ?.short_name || ''
        const city =
          components.find(
            (component) =>
              component.types.includes('locality') ||
              component.types.includes('administrative_area_level_2'),
          )?.long_name || ''
        const stateProvince =
          components.find((component) =>
            component.types.includes('administrative_area_level_1'),
          )?.short_name || ''
        const zipCode =
          components.find((component) =>
            component.types.includes('postal_code'),
          )?.long_name || ''

        // Auto-populate the street address field
        const formattedStreetAddress = `${streetNumber} ${route}`.trim()
        if (formattedStreetAddress) {
          newClinicFormik.setFieldValue('street', formattedStreetAddress)
        }

        // Auto-populate the other form fields
        if (city) {
          newClinicFormik.setFieldValue('city', city)
        }
        if (stateProvince) {
          newClinicFormik.setFieldValue('stateProvince', stateProvince)
        }
        if (zipCode) {
          newClinicFormik.setFieldValue('zipCode', zipCode)
        }

        // Set country based on Google Places result
        if (country === 'US') {
          newClinicFormik.setFieldValue('country', 'united_states_of_america')
        } else if (country === 'CA') {
          newClinicFormik.setFieldValue('country', 'canada')
        }
      }
    }
  }, [googlePlace, placeSelected])

  // Show loading overlay during clinic creation
  if (isCreatingClinic) {
    return (
      <Box className={classes.formCard}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          width="100%"
        >
          <CircularProgress size={40} style={{ marginBottom: 16 }} />
          <Box
            style={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#212121',
              marginBottom: 8,
            }}
          >
            Creating Your Clinic...
          </Box>
          <Box
            style={{
              fontSize: '14px',
              color: '#6B7280',
              textAlign: 'center',
            }}
          >
            Please wait while we set up your clinic and activate your account.
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box className={classes.formCard}>
      <FormHeader
        title="Create New Clinic"
        description="Complete the form below to set up your new clinic profile."
        rightButton={
          <Button
            variant="text"
            startIcon={<LogOut size={16} />}
            onClick={handleSignOut}
            style={{
              color: '#6B7280',
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            Sign Out
          </Button>
        }
      />

      {submitError && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {submitError}
        </Alert>
      )}

      <FormContainer>
        <form onSubmit={newClinicFormik.handleSubmit}>
          <Grid container rowSpacing={0.5} columnSpacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="name"
                label="Clinic Name"
                variant="outlined"
                placeholder="Enter the name of your clinic"
                required
                value={newClinicFormik.values.name}
                onChange={newClinicFormik.handleChange}
                onBlur={newClinicFormik.handleBlur}
                error={
                  newClinicFormik.touched.name &&
                  Boolean(newClinicFormik.errors.name)
                }
                helperText={
                  newClinicFormik.touched.name && newClinicFormik.errors.name
                }
                className={classes.inputField}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="shortName"
                label="Short Name"
                variant="outlined"
                placeholder="Clinic name used in messages to patients"
                required
                value={newClinicFormik.values.shortName}
                onChange={newClinicFormik.handleChange}
                onBlur={newClinicFormik.handleBlur}
                error={
                  newClinicFormik.touched.shortName &&
                  Boolean(newClinicFormik.errors.shortName)
                }
                helperText={
                  newClinicFormik.touched.shortName &&
                  newClinicFormik.errors.shortName
                }
                className={classes.inputField}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                disableClearable
                options={completeManually ? [] : placePredictions}
                getOptionLabel={(opt) => opt.description || ''}
                onBlur={() => newClinicFormik.setFieldTouched('searchAddress')}
                onChange={(_event, place) => {
                  if (completeManually) {
                    newClinicFormik.setFieldValue('searchAddress', place)
                  } else {
                    placesService?.getDetails(
                      {
                        placeId: place.place_id,
                        fields: ['address_components'],
                      },
                      (placeDetails: PlaceResult | null) => {
                        setGooglePlace(placeDetails)
                        setPlaceSelected(true)
                      },
                    )
                  }
                }}
                onInputChange={(_event, searchAddress) => {
                  if (_event && _event.type === 'change') {
                    newClinicFormik.setFieldValue(
                      'searchAddress',
                      searchAddress,
                    )
                  }
                  if (completeManually) {
                    newClinicFormik.setFieldValue(
                      'searchAddress',
                      searchAddress,
                    )
                  } else {
                    debouncedGetPlacePredictions(searchAddress)
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    name="searchAddress"
                    label="Clinic Address (Auto-Complete)"
                    placeholder="Begin typing your address — results will appear automatically."
                    variant="outlined"
                    error={
                      newClinicFormik.touched.searchAddress &&
                      Boolean(newClinicFormik.errors.searchAddress)
                    }
                    helperText={
                      newClinicFormik.touched.searchAddress &&
                      newClinicFormik.errors.searchAddress
                    }
                    className={classes.inputField}
                  />
                )}
              />
            </Grid>

            {/* Rest of form fields */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="street"
                label="Street Address"
                placeholder="Street Address"
                variant="outlined"
                required
                value={newClinicFormik.values.street}
                onChange={newClinicFormik.handleChange}
                onBlur={newClinicFormik.handleBlur}
                error={
                  newClinicFormik.touched.street &&
                  Boolean(newClinicFormik.errors.street)
                }
                helperText={
                  newClinicFormik.touched.street &&
                  newClinicFormik.errors.street
                }
                className={classes.inputField}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="unitApartmentSuite"
                label="Unit/Suite (Optional)"
                variant="outlined"
                value={newClinicFormik.values.unitApartmentSuite}
                onChange={newClinicFormik.handleChange}
                onBlur={newClinicFormik.handleBlur}
                className={classes.inputField}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="city"
                label="City"
                variant="outlined"
                placeholder="City"
                required
                value={newClinicFormik.values.city}
                onChange={newClinicFormik.handleChange}
                onBlur={newClinicFormik.handleBlur}
                error={
                  newClinicFormik.touched.city &&
                  Boolean(newClinicFormik.errors.city)
                }
                helperText={
                  newClinicFormik.touched.city && newClinicFormik.errors.city
                }
                className={classes.inputField}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="stateProvince"
                label="State/Province"
                variant="outlined"
                placeholder="State"
                required
                value={newClinicFormik.values.stateProvince}
                onChange={newClinicFormik.handleChange}
                onBlur={newClinicFormik.handleBlur}
                error={
                  newClinicFormik.touched.stateProvince &&
                  Boolean(newClinicFormik.errors.stateProvince)
                }
                helperText={
                  newClinicFormik.touched.stateProvince &&
                  newClinicFormik.errors.stateProvince
                }
                className={classes.inputField}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="zipCode"
                label="ZIP/Postal Code"
                variant="outlined"
                placeholder="Zip"
                required
                value={newClinicFormik.values.zipCode}
                onChange={newClinicFormik.handleChange}
                onBlur={newClinicFormik.handleBlur}
                error={
                  newClinicFormik.touched.zipCode &&
                  Boolean(newClinicFormik.errors.zipCode)
                }
                helperText={
                  newClinicFormik.touched.zipCode &&
                  newClinicFormik.errors.zipCode
                }
                className={classes.inputField}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={countryList}
                getOptionLabel={(option) => option.label}
                value={
                  countryList.find(
                    (option) => option.value === newClinicFormik.values.country,
                  ) || null
                }
                onChange={(_, newValue) => {
                  newClinicFormik.setFieldValue(
                    'country',
                    newValue?.value || '',
                  )
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="country"
                    label="Country"
                    variant="outlined"
                    required
                    error={
                      newClinicFormik.touched.country &&
                      Boolean(newClinicFormik.errors.country)
                    }
                    helperText={
                      newClinicFormik.touched.country &&
                      newClinicFormik.errors.country
                    }
                    className={classes.inputField}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <div className={classes.phoneFieldContainer}>
                <FormLabel required>Phone Number</FormLabel>
                <PhoneInput
                  inputProps={{
                    name: 'phone',
                    required: false,
                  }}
                  inputStyle={{
                    width: '100%',
                    border: '1px solid #E1E4E8',
                    backgroundColor: '#FAFAFB',
                    borderRadius: '8px',
                    padding: '16px',
                    paddingLeft: '58px',
                    fontSize: 'inherit',

                    boxSizing: 'border-box',
                  }}
                  buttonStyle={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    height: '48px', // Match input height
                  }}
                  dropdownStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E1E4E8',
                    borderRadius: '8px',
                  }}
                  onlyCountries={['us', 'ca', 'gb']}
                  preserveOrder={['onlyCountries']}
                  country={
                    newClinicFormik.values.country === 'canada'
                      ? 'ca'
                      : newClinicFormik.values.country === 'united_kingdom'
                        ? 'gb'
                        : 'us'
                  }
                  enableSearch={true}
                  disableSearchIcon={true}
                  value={newClinicFormik.values.phone}
                  onChange={(phone) => {
                    newClinicFormik.setFieldValue('phone', phone)
                  }}
                  onBlur={() => {
                    newClinicFormik.setFieldTouched('phone')
                  }}
                />
                {newClinicFormik.touched.phone &&
                  newClinicFormik.errors.phone && (
                    <div
                      style={{
                        color: '#f44336',
                        fontSize: '1em',
                        marginTop: '3px',
                      }}
                    >
                      {newClinicFormik.errors.phone}
                    </div>
                  )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={
                  newClinicFormik.values.country === 'canada'
                    ? canadaTimeZones
                    : unitedStatesTimeZones
                }
                getOptionLabel={(option) => option}
                value={newClinicFormik.values.timeZone || null}
                onChange={(_, newValue) => {
                  newClinicFormik.setFieldValue('timeZone', newValue || '')
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="timeZone"
                    label="Time Zone"
                    placeholder="Time Zone"
                    variant="outlined"
                    required
                    error={
                      newClinicFormik.touched.timeZone &&
                      Boolean(newClinicFormik.errors.timeZone)
                    }
                    helperText={
                      newClinicFormik.touched.timeZone &&
                      newClinicFormik.errors.timeZone
                    }
                    className={classes.inputField}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={clinicTypes}
                getOptionLabel={(option) => option}
                value={newClinicFormik.values.clinicType || null}
                onChange={(_, newValue) => {
                  newClinicFormik.setFieldValue('clinicType', newValue || '')
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="clinicType"
                    label="Clinic Type"
                    placeholder="Select clinic"
                    variant="outlined"
                    required
                    error={
                      newClinicFormik.touched.clinicType &&
                      Boolean(newClinicFormik.errors.clinicType)
                    }
                    helperText={
                      newClinicFormik.touched.clinicType &&
                      newClinicFormik.errors.clinicType
                    }
                    className={classes.inputField}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                disablePortal
                open={specialtyDropdownOpen}
                onOpen={() => {
                  setSpecialtyDropdownOpen(true)
                }}
                onClose={() => {
                  setSpecialtyDropdownOpen(false)
                }}
                options={siteSpecialties}
                getOptionLabel={(option) => option}
                value={newClinicFormik.values.specialty}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    flexWrap: 'wrap',
                    overflowX: 'hidden',
                    '& input': {
                      minWidth: '30px',
                      flexGrow: 1,
                    },
                  },
                  '& .MuiAutocomplete-inputRoot': {
                    flexWrap: 'wrap',
                  },
                }}
                onChange={(_, newValue) => {
                  newClinicFormik.setFieldValue('specialty', newValue || [])

                  // Auto-close dropdown and remove focus when all options are selected
                  if (newValue && newValue.length === siteSpecialties.length) {
                    setSpecialtyDropdownOpen(false)
                    setTimeout(() => {
                      specialtyInputRef.current?.blur()
                    }, 100)
                  }
                }}
                onBlur={() => {
                  newClinicFormik.setFieldTouched('specialty')
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={specialtyInputRef}
                    name="specialty"
                    label="Specialty"
                    placeholder={
                      newClinicFormik.values.specialty.length === 0
                        ? 'Select specialties'
                        : ''
                    }
                    variant="outlined"
                    error={
                      newClinicFormik.touched.specialty &&
                      Boolean(newClinicFormik.errors.specialty)
                    }
                    helperText={
                      newClinicFormik.touched.specialty &&
                      newClinicFormik.errors.specialty
                    }
                    className={classes.inputField}
                    InputProps={{
                      ...params.InputProps,
                      style: {
                        alignItems: 'flex-start',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                      },
                    }}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      shrink: true,
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index })
                    return (
                      <Chip
                        key={index}
                        {...tagProps}
                        variant="outlined"
                        label={option}
                        size="small"
                        style={{
                          margin: '2px 4px 2px 0',
                          fontSize: '12px',
                          height: '24px',
                          borderRadius: '12px',
                          backgroundColor: '#F3F4F6',
                          border: '1px solid #D1D5DB',
                          color: '#374151',
                        }}
                      />
                    )
                  })
                }
              />
            </Grid>
          </Grid>

          <Box display="flex" gap={2} marginTop={3}>
            <Button
              variant="outlined"
              onClick={handleGoBack}
              disabled={newClinicFormik.isSubmitting}
              style={{
                flex: 1,
                borderRadius: '8px',
              }}
            >
              Back
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={newClinicFormik.isSubmitting}
              style={{
                backgroundColor: '#1B3A52',
                flex: 1,
                borderRadius: '8px',
              }}
            >
              {newClinicFormik.isSubmitting ? 'Creating...' : 'Create Clinic'}
            </Button>
          </Box>
        </form>
      </FormContainer>
    </Box>
  )
}

export default CreateNewClinicStep
